import { useState, useEffect } from 'react'
import SignaturePad from './SignaturePad'
import { apiService } from '../services/apiService'

interface CUSMAItem {
  description: string
  htsCode: string
  countryOfOrigin: string
  quantity: number
  sku: string
}

interface SavedSignatureData {
  signature_data?: string
  signer_name?: string
  signer_title?: string
}

interface CUSMAFormModalProps {
  isOpen: boolean
  onClose: () => void
  shipmentId: string
  items: CUSMAItem[]
  isStaff?: boolean
  clientId?: string
}

export default function CUSMAFormModal({
  isOpen,
  onClose,
  shipmentId,
  items,
  isStaff = false,
  clientId
}: CUSMAFormModalProps) {
  const [signatureData, setSignatureData] = useState<string>('')
  const [signerName, setSignerName] = useState<string>('')
  const [signerTitle, setSignerTitle] = useState<string>('')
  const [originCriteria, setOriginCriteria] = useState<{[sku: string]: string}>({})
  const [blanketStartDate, setBlanketStartDate] = useState<string>('')
  const [blanketEndDate, setBlanketEndDate] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)

  // Initialize data when modal opens
  useEffect(() => {
    if (!isOpen) return

    // Initialize blanket period dates immediately
    const today = new Date()
    const oneYearFromToday = new Date(today.getTime() + 365 * 24 * 60 * 60 * 1000)
    setBlanketStartDate(today.toISOString().split('T')[0])
    setBlanketEndDate(oneYearFromToday.toISOString().split('T')[0])

    // Initialize origin criteria to 'A' for all eligible items
    const initialCriteria: {[sku: string]: string} = {}
    items.forEach(item => {
      initialCriteria[item.sku] = 'A'
    })
    setOriginCriteria(initialCriteria)

    // Load saved signature data
    const loadSavedSignatureData = async () => {
      try {
        setLoading(true)
        const endpoint = isStaff && clientId
          ? `/api/staff/clients/${clientId}/cusma/signature-data`
          : '/api/cusma/signature-data'

        const response = await apiService.get(endpoint)

        // Handle both cases: response.data exists or is null (no saved signature)
        if (response.success) {
          const saved: SavedSignatureData = response.data || {}
          setSignatureData(saved.signature_data || '')
          setSignerName(saved.signer_name || '')
          setSignerTitle(saved.signer_title || '')
        }

      } catch (error) {
        console.error('Error loading saved signature data:', error)
        // Continue anyway - user can still fill in the form
      } finally {
        setLoading(false)
      }
    }

    loadSavedSignatureData()
  }, [isOpen, isStaff, clientId, items])

  const handleGeneratePDF = async () => {
    if (!signatureData || !signerName || !signerTitle) {
      alert('Please complete the signature, name, and title fields.')
      return
    }

    try {
      setGenerating(true)

      const endpoint = isStaff && clientId
        ? `/api/staff/clients/${clientId}/shipments/${shipmentId}/cusma/generate-pdf`
        : `/api/shipments/${shipmentId}/cusma/generate-pdf`

      const requestData = {
        signatureData,
        signerName,
        signerTitle,
        originCriteria,
        blanketPeriod: {
          startDate: blanketStartDate,
          endDate: blanketEndDate
        }
      }

      // Make request to generate PDF using proper authentication
      const token = localStorage.getItem('yyz-cbp-token')
      if (!token) {
        alert('Authentication required. Please log in again.')
        return
      }

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestData)
      })

      if (response.ok) {
        // PDF was generated successfully, trigger download
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.style.display = 'none'
        a.href = url
        a.download = `CUSMA-Certificate-${shipmentId}-${new Date().toISOString().split('T')[0]}.pdf`
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)

        // Close modal after successful generation
        onClose()
      } else {
        let errorMessage = 'Unknown error'
        try {
          const errorData = await response.json()
          errorMessage = errorData.error || `Server error: ${response.status} ${response.statusText}`
        } catch (parseError) {
          errorMessage = `Server error: ${response.status} ${response.statusText}`
        }

        if (response.status === 401) {
          errorMessage = 'Authentication failed. Please log in again.'
        }

        alert(`Error generating PDF: ${errorMessage}`)
      }

    } catch (error) {
      console.error('Error generating CUSMA PDF:', error)
      alert('Error generating PDF. Please try again.')
    } finally {
      setGenerating(false)
    }
  }

  const handleSignatureChange = async (signature: string | null) => {
    console.log('Signature changed:', signature ? 'Signature present' : 'No signature')
    setSignatureData(signature || '')

    // Auto-save signature data when all fields are present
    if (signature && signerName.trim() && signerTitle.trim()) {
      try {
        const endpoint = isStaff && clientId
          ? `/api/staff/clients/${clientId}/cusma/signature-data`
          : '/api/cusma/signature-data'

        await apiService.post(endpoint, {
          signatureData: signature,
          signerName,
          signerTitle
        })

        console.log('✅ Signature data auto-saved successfully')
      } catch (error) {
        console.error('⚠️ Failed to auto-save signature data:', error)
        // Don't show error to user - it will be saved during PDF generation as fallback
      }
    }
  }

  const handleSignerNameChange = async (newName: string) => {
    setSignerName(newName)

    // Auto-save when all fields are present
    if (signatureData && newName.trim() && signerTitle.trim()) {
      try {
        const endpoint = isStaff && clientId
          ? `/api/staff/clients/${clientId}/cusma/signature-data`
          : '/api/cusma/signature-data'

        await apiService.post(endpoint, {
          signatureData,
          signerName: newName,
          signerTitle
        })

        console.log('✅ Signature data auto-saved successfully (name changed)')
      } catch (error) {
        console.error('⚠️ Failed to auto-save signature data:', error)
      }
    }
  }

  const handleSignerTitleChange = async (newTitle: string) => {
    setSignerTitle(newTitle)

    // Auto-save when all fields are present
    if (signatureData && signerName.trim() && newTitle.trim()) {
      try {
        const endpoint = isStaff && clientId
          ? `/api/staff/clients/${clientId}/cusma/signature-data`
          : '/api/cusma/signature-data'

        await apiService.post(endpoint, {
          signatureData,
          signerName,
          signerTitle: newTitle
        })

        console.log('✅ Signature data auto-saved successfully (title changed)')
      } catch (error) {
        console.error('⚠️ Failed to auto-save signature data:', error)
      }
    }
  }

  const handleOriginCriteriaChange = (sku: string, criteria: string) => {
    setOriginCriteria(prev => ({
      ...prev,
      [sku]: criteria
    }))
  }

  if (!isOpen) return null

  const eligibleItems = items.filter(item => {
    const coo = item.countryOfOrigin?.toUpperCase()
    return coo === 'US' || coo === 'CA' || coo === 'MX' ||
           coo === 'UNITED STATES' || coo === 'CANADA' || coo === 'MEXICO'
  })

  if (eligibleItems.length === 0) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '30px',
          borderRadius: '8px',
          maxWidth: '500px',
          margin: '20px'
        }}>
          <h2>No CUSMA Eligible Items</h2>
          <p>This shipment does not contain any items eligible for CUSMA preferential treatment. Only items with Country of Origin of US, CA, or MX are eligible.</p>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      overflow: 'auto'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '8px',
        maxWidth: '800px',
        maxHeight: '90vh',
        overflow: 'auto',
        margin: '20px'
      }}>
        <h2 style={{ marginBottom: '20px' }}>CUSMA Certificate of Origin</h2>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            Loading saved signature data...
          </div>
        ) : (
          <>
            {/* Eligible Items Section */}
            <div style={{ marginBottom: '25px' }}>
              <h3>Eligible Items ({eligibleItems.length})</h3>
              <div style={{ maxHeight: '200px', overflow: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>SKU</th>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Description</th>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Country</th>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Qty</th>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Origin Criteria</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eligibleItems.map((item, index) => (
                      <tr key={index}>
                        <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{item.sku}</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{item.description}</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{item.countryOfOrigin}</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{item.quantity}</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                          <select
                            value={originCriteria[item.sku] || 'A'}
                            onChange={(e) => handleOriginCriteriaChange(item.sku, e.target.value)}
                            style={{ width: '100%', padding: '4px' }}
                          >
                            <option value="A">A - Wholly obtained</option>
                            <option value="B">B - Produced exclusively from originating materials</option>
                            <option value="C">C - Satisfies applicable rule of origin</option>
                            <option value="D">D - De minimis</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Blanket Period */}
            <div style={{ marginBottom: '25px' }}>
              <h3>Blanket Period</h3>
              <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
                This certificate covers shipments during the specified period.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={blanketStartDate}
                    onChange={(e) => setBlanketStartDate(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    End Date
                  </label>
                  <input
                    type="date"
                    value={blanketEndDate}
                    onChange={(e) => setBlanketEndDate(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Signer Information */}
            <div style={{ marginBottom: '25px' }}>
              <h3>Certifier Information</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Signer Name *
                  </label>
                  <input
                    type="text"
                    value={signerName}
                    onChange={(e) => handleSignerNameChange(e.target.value)}
                    placeholder="Enter your full name"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: !signerName.trim() ? '2px solid #dc3545' : '1px solid #ddd',
                      borderRadius: '4px',
                      outline: !signerName.trim() ? 'none' : undefined
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Title *
                  </label>
                  <input
                    type="text"
                    value={signerTitle}
                    onChange={(e) => handleSignerTitleChange(e.target.value)}
                    placeholder="e.g., Export Manager, CEO"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: !signerTitle.trim() ? '2px solid #dc3545' : '1px solid #ddd',
                      borderRadius: '4px',
                      outline: !signerTitle.trim() ? 'none' : undefined
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Signature Section */}
            <div style={{ marginBottom: '25px' }}>
              <h3>Digital Signature *</h3>
              <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
                By signing below, I certify that the goods described qualify as originating and the information is true and accurate.
              </p>
              <SignaturePad
                initialSignature={signatureData}
                onSignatureChange={handleSignatureChange}
                width={700}
                height={150}
                showError={!signatureData}
              />
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={onClose}
                disabled={generating}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: generating ? 'not-allowed' : 'pointer',
                  opacity: generating ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleGeneratePDF}
                disabled={!signatureData || !signerName.trim() || !signerTitle.trim() || generating}
                style={{
                  padding: '12px 24px',
                  backgroundColor: (!signatureData || !signerName.trim() || !signerTitle.trim() || generating) ? '#6c757d' : '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: (!signatureData || !signerName.trim() || !signerTitle.trim() || generating) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                title={
                  !signatureData ? 'Please draw a signature' :
                  !signerName.trim() ? 'Please enter signer name' :
                  !signerTitle.trim() ? 'Please enter signer title' :
                  generating ? 'Generating PDF...' :
                  'Generate & Download PDF'
                }
              >
                {generating && (
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid transparent',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                )}
                {generating ? 'Generating PDF...' : 'Generate & Download PDF'}
              </button>
            </div>
          </>
        )}

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  )
}